import React, { useEffect, useMemo, useRef, useState } from "react";

/** Exported so AddEditQuestion can reuse the type */
export type AtcLine = {
  id: string;
  role: "ATC" | "PILOT" | "HINT";
  text: string;
  delay_ms?: number;
  message: string;
  tts?: boolean;
  heading: string;            // e.g., "Who you are talking to"
  align?: "left" | "right";
  style?: "line" | "arrow";
  expected_any_of?: string[];
};

type Props = {
  vb: { w: number; h: number };
  script: AtcLine[];
  autoplay?: boolean;
  radioSpeak?: (text: string) => void;
  visible?: boolean;
};

const ScriptOverlaySvg: React.FC<Props> = ({
  vb,
  script,
  autoplay = false,
  radioSpeak,
  visible = true,
}) => {
  const [visibleIds, setVisibleIds] = useState<string[]>([]);

  useEffect(() => {
    if (!visible || !script?.length) {
      setVisibleIds([]);
      return;
    }
    if (!autoplay) {
      setVisibleIds(script.map((l) => l.id));
      return;
    }
    let alive = true;
    setVisibleIds([]);
    let t = 0;
    script.forEach((line) => {
      t += line.delay_ms ?? 0;
      window.setTimeout(() => {
        if (!alive) return;
        setVisibleIds((v) => (v.includes(line.id) ? v : [...v, line.id]));
        if (line.tts && line.text) radioSpeak?.(line.text);
      }, Math.max(0, t));
    });
    return () => {
      alive = false;
    };
  }, [script, autoplay, visible, radioSpeak]);

  const sizes = useMemo(() => {
    // ---- Bubble width scales with SVG width. Tweak here if you want bigger/smaller boxes.
    // Wider box so the ATC script is bigger (was 0.20 / max 420).
    const bubbleW = Math.max(160, Math.min(vb.w * 0.30, 800));

    const baseH = Math.max(40, Math.min(110, vb.h * 0.06)); // used for initial scale references
    const gap = Math.max(12, baseH * 0.2);

    // Font in **user units** (viewBox). This makes text scale with the SVG.
    // Bumped 1.25x so the script text reads bigger (matches the player overlay).
    const font = Math.round(Math.max(vb.h * 0.02, Math.min(vb.h * 0.03, 34)) * 1.25);

    const corner = Math.max(8, Math.min(16, baseH * 0.18));
    const stripe = Math.max(3, Math.min(6, baseH * 0.06));

    // ---- Distance from center. We reduce by 10 here per your request.
    // If you want to tweak later, just change CENTER_GAP_ADJUST.
    const CENTER_GAP_ADJUST = -40; // <— reduce center gap by 10
    const basePadFromCenter = Math.max(16, vb.w * 0.03);
    const padX = Math.max(0, basePadFromCenter + CENTER_GAP_ADJUST);

    const innerPadX = 18;
    const innerPadY = Math.max(8, Math.round(font * 0.55));
    const lineHeight = Math.round(font * 1.24);
    const tailW = Math.max(14, Math.min(22, baseH * 0.22));
    const tailH = Math.max(16, Math.min(24, baseH * 0.26));
    return {
      bubbleW,
      gap,
      font,
      corner,
      stripe,
      padX,
      innerPadX,
      innerPadY,
      lineHeight,
      tailW,
      tailH,
    };
  }, [vb.w, vb.h]);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  if (!visible || !script?.length) return null;

  const startY = Math.max(vb.h * 0.06, 40); // start near the top (was 0.18 / 120)
  const centerX = vb.w / 2;

  const colorByRole: Record<
    AtcLine["role"],
    { fill: string; stripe: string; text: string }
  > = {
    ATC: { fill: "#9aa6b2", stripe: "#a8a9ac", text: "#ffffff" },
    PILOT: { fill: "#2563eb", stripe: "#4487fb", text: "#ffffff" },
    HINT: { fill: "rgba(148,163,184,0.7)", stripe: "#a8a9ac", text: "#ffffff" },
  };

  // --- text measurement for wrapping
  if (!canvasRef.current) canvasRef.current = document.createElement("canvas");
  const ctx = canvasRef.current.getContext("2d")!;
  const setFontOnCtx = (fontPx: number) => {
    // Convert user-unit font to approx px for measuring:
    // Heuristic: assume viewBox height ~ 1000 == 1000px (relative scaling).
    // We map user units to px by using current viewport height ratio if needed.
    // Simpler: treat font as px for measurement—close enough for wrapping decisions.
    ctx.font = `bold ${Math.max(12, Math.round(fontPx))}px Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif`;
  };

  const wrapText = (text: string, maxWidth: number, fontUnit: number): string[] => {
    setFontOnCtx(fontUnit);
    const words = (text || "").split(/\s+/);
    const lines: string[] = [];
    let cur = "";
    for (const w of words) {
      const trial = cur ? `${cur} ${w}` : w;
      if (ctx.measureText(trial).width <= maxWidth) {
        cur = trial;
      } else {
        if (cur) lines.push(cur);
        if (ctx.measureText(w).width > maxWidth) {
          let chunk = "";
          for (const ch of w) {
            const trial2 = chunk + ch;
            if (ctx.measureText(trial2).width <= maxWidth) chunk = trial2;
            else {
              if (chunk) lines.push(chunk);
              chunk = ch;
            }
          }
          cur = chunk;
        } else {
          cur = w;
        }
      }
    }
    if (cur) lines.push(cur);
    return lines.length ? lines : [""];
  };

  let cursorY = startY;

  return (
    <g aria-label="atc-script-overlay">
      {script.map((line) => {
        const show = visibleIds.includes(line.id);
        if (!show) return null;

        const c = colorByRole[line.role] || colorByRole.ATC;
        const align = line.align ?? "left";
        const styleKind = line.style ?? "line";

        // content width for wrapping (in user units).
        // "line" text starts at (stripe + innerPadX + 12) from the left edge, so we
        // must subtract that left offset AND a right padding (innerPadX) — otherwise
        // wrapped lines run past the bubble's right edge.
        const contentW =
          sizes.bubbleW -
          (styleKind === "line"
            ? sizes.stripe + sizes.innerPadX * 2 + 12
            : sizes.innerPadX * 2);

        const rows = wrapText(line.text || "", contentW, sizes.font);
        const bubbleH = sizes.innerPadY * 2 + rows.length * sizes.lineHeight;

        // position relative to center, using padX (already adjusted by -10)
        const baseX =
          align === "left"
            ? centerX - sizes.padX - sizes.bubbleW
            : centerX + sizes.padX;

        const y = cursorY;
        cursorY += bubbleH + sizes.gap;

        if (styleKind === "line") {
          const stripeX = baseX + 16;
          const textX = baseX + sizes.stripe + sizes.innerPadX + 12;
          const firstTextY = y + sizes.innerPadY + sizes.lineHeight * 0.85;

          return (
            <g key={line.id} className="svgItem">
              <rect
                x={baseX}
                y={y}
                width={sizes.bubbleW}
                height={bubbleH}
                rx={sizes.corner}
                ry={sizes.corner}
                fill={c.fill}
                opacity={0.95}
              />
              <line
                x1={stripeX}
                y1={y + Math.max(8, bubbleH * 0.15)}
                x2={stripeX}
                y2={y + bubbleH - Math.max(8, bubbleH * 0.15)}
                stroke={c.stripe}
                strokeWidth={sizes.stripe}
              />
              <text
                x={textX}
                y={firstTextY}
                fill={c.text}
                fontFamily="Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif"
                fontSize={sizes.font} 
                fontWeight="bold"
                style={{ userSelect: "none" as const }}
              >
                {rows.map((r, idx) => (
                  <tspan key={idx} x={textX} dy={idx === 0 ? 0 : sizes.lineHeight}>
                    {r}
                  </tspan>
                ))}
              </text>
            </g>
          );
        }

        // ARROW style
        const tailMidY = y + bubbleH / 2;
        const tailX = align === "left" ? baseX : baseX + sizes.bubbleW;
        const poly =
          align === "left"
            ? `${tailX},${tailMidY} ${tailX - sizes.tailW},${
                tailMidY - sizes.tailH / 2
              } ${tailX - sizes.tailW},${tailMidY + sizes.tailH / 2}`
            : `${tailX},${tailMidY} ${tailX + sizes.tailW},${
                tailMidY - sizes.tailH / 2
              } ${tailX + sizes.tailW},${tailMidY + sizes.tailH / 2}`;

        const textX = baseX + sizes.innerPadX;
        const firstTextY = y + sizes.innerPadY + sizes.lineHeight * 0.85;

        return (
          <g key={line.id} className="svgItem">
            <rect
              x={baseX}
              y={y}
              width={sizes.bubbleW}
              height={bubbleH}
              rx={sizes.corner}
              ry={sizes.corner}
              fill={c.fill}
              opacity={0.95}
            />
            <polygon points={poly} fill={c.fill} opacity={0.95} />
            <text
              x={textX}
              y={firstTextY}
              fill={c.text}
              fontFamily="Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif"
              fontSize={sizes.font}
              fontWeight="bold"
              style={{ userSelect: "none" as const }}
            >
              {rows.map((r, idx) => (
                <tspan key={idx} x={textX} dy={idx === 0 ? 0 : sizes.lineHeight}>
                  {r}
                </tspan>
              ))}
            </text>
          </g>
        );
      })}
    </g>
  );
};

export default ScriptOverlaySvg;
